import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { CloudUpload, Close, Delete, CheckCircle } from '@mui/icons-material';
import { gql, useMutation } from '@apollo/client';
import { Search, Download } from '@mui/icons-material';
import { CSVLink } from 'react-csv';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void;
}

interface UploadResponse {
  totalRecords: number;
  successfullyUploaded: number;
  totalFailedRecords: number;
  totalDuplicatedRecords: number;
  deviceUploads: Array<{
    IMEI: string;
    serialNo: string;
    deviceMake: string;
    error: string;
  }>;
}

const GET_URL = gql`
  mutation GetPublicExcelUrl($file: Upload!) {
    getPublicExcelUrl(file: $file) {
      url
    }
  }
`;

const BULK_UPLOAD_DEVICES = gql`
  mutation BulkUploadDevice($url: String!) {
    bulkUploadDevice(url: $url) {
      status
      message
      summary {
        totalEntries
        successEntries
        duplicateEntries
        totalFailedEntries
        nonValidEntries
        faultyEntries
      }
    }
  }
`;

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({ open, onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadUrl, setUploadUrl] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showResponse, setShowResponse] = useState(false);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterError, setFilterError] = useState<string>('all');
  const [showSearch, setShowSearch] = useState(false);

  const [getUploadUrl, { loading: uploadingFile }] = useMutation(GET_URL);
  const [uploadDevices, { loading: uploadingDevices }] = useMutation(BULK_UPLOAD_DEVICES);

  const isUploading = uploadingFile || uploadingDevices;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx')) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Only .xlsx files are allowed.');
      setSnackbarOpen(true);
      return;
    }

    setFile(selectedFile);
    setUploadUrl('');

    try {
      const { data } = await getUploadUrl({ variables: { file: selectedFile } });
      const url = data?.getPublicExcelUrl?.url;
      if (url) {
        setUploadUrl(url);
        setSnackbarSeverity('success');
        setSnackbarMessage('File uploaded successfully. Ready to upload.');
        setSnackbarOpen(true);
      } else {
        throw new Error('Failed to retrieve file URL.');
      }
    } catch (err) {
      console.error('GET_URL Error:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('File upload failed. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadUrl) return;

    try {
      const { data } = await uploadDevices({ variables: { url: uploadUrl } });

      const status = data?.bulkUploadDevice?.status;
      const message = data?.bulkUploadDevice?.message;
      const summary = data?.bulkUploadDevice?.summary;
      const errors = data?.bulkUploadDevice?.errors || [];

      if (status === true) {
        setSnackbarSeverity('success');
        setSnackbarMessage(message || 'Upload successful');

        const response: UploadResponse = {
          totalRecords: summary?.totalEntries || 0,
          successfullyUploaded: summary?.successEntries || 0,
          totalFailedRecords: summary?.totalFailedEntries || 0,
          totalDuplicatedRecords: summary?.duplicateEntries || 0,
          deviceUploads: summary?.faultyEntries?.map((entry: any) => ({
            IMEI: entry.imei,
            serialNo: entry.serial_no,
            deviceMake: entry.device_make,
            error: entry.error
          }))
        };

        setUploadResponse(response);
        setShowResponse(true);
        onUploaded();
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(message || 'Upload failed');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Upload Error:', err);
      setSnackbarSeverity('error');
      setSnackbarMessage('Upload failed. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleClose = () => {
    // Reset all state when closing
    setFile(null);
    setUploadUrl('');
    setUploadResponse(null);
    setShowResponse(false);
    setSearchTerm('');
    setFilterError('all');
    setShowSearch(false);
    onClose();
  };

  const handleResponseClose = () => {
    // Reset all state when closing the response dialog
    setFile(null);
    setUploadUrl('');
    setUploadResponse(null);
    setShowResponse(false);
    setSearchTerm('');
    setFilterError('all');
    setShowSearch(false);
    onClose();
  };

  const filteredData = useMemo(() => {
    if (!uploadResponse?.deviceUploads) return [];

    let data = [...uploadResponse.deviceUploads];
    if (filterError !== 'all') {
      data = data.filter(device => {
        const errorText = String(device.error ?? '').toLowerCase();

        switch (filterError) {
          case 'imei':
            return errorText.includes('imei');
          case 'serial':
            return errorText.includes('serial');
          case 'duplicate':
            return errorText.includes('duplicate');
          default:
            return true;
        }
      });
    }
    if (searchTerm.trim() !== '') {
      const term = searchTerm.trim().toLowerCase();

      data = data.filter(device =>
        String(device.IMEI ?? '').toLowerCase().includes(term) ||
        String(device.serialNo ?? '').toLowerCase().includes(term)
      );
    }

    return data;
  }, [uploadResponse, searchTerm, filterError]);

  const csvData = useMemo(() => {
    if (!uploadResponse) return [];
    return filteredData.map(device => ({
      IMEI: device.IMEI,
      serialNo: device.serialNo,
      deviceMake: device.deviceMake,
      error: device.error
    }));
  }, [filteredData, uploadResponse]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Bulk Upload Devices
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'inherit', position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upload File
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ccc',
                borderRadius: 2,
                px: 2,
                py: 1,
                background: '#fff',
                minHeight: 56,
              }}
            >
              <input
                id="bulk-upload-file"
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <label htmlFor="bulk-upload-file">
                <Button
                  variant="contained"
                  component="span"
                  sx={{
                    background: '#000',
                    color: '#fff',
                    textTransform: 'none',
                    mr: 2,
                    minWidth: 120,
                    '&:hover': { background: '#333' }
                  }}
                  disabled={isUploading}
                >
                  Choose File
                </Button>
              </label>
              {file ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#222', fontSize: 16 }}>
                      {file.name}
                    </Typography>
                    {uploadUrl && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <CheckCircle fontSize="small" sx={{ color: 'green' }} />
                        <Typography variant="body2" color="green">
                          File uploaded successfully
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    onClick={() => {
                      setFile(null);
                      setUploadUrl('');
                    }}
                    sx={{ color: '#E60012' }}
                    title="Remove file"
                  >
                    <Delete />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: 1, color: '#888', fontSize: 16 }}>
                    No file chosen
                  </Typography>
                  <IconButton disabled>
                    <CloudUpload sx={{ color: '#888' }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
          {isUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isUploading}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadUrl || isUploading}
            sx={{
              backgroundColor: '#E60012',
              '&:hover': { backgroundColor: '#cc0010' },
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={showResponse} onClose={handleResponseClose} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            position: 'relative',
            pr: 8,
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Upload Response
          </Typography>
          <IconButton
            onClick={handleResponseClose}
            size="small"
            sx={{
              color: 'inherit',
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {uploadResponse && (
            <Box>
              <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                <strong>Total Records:</strong> {uploadResponse.totalRecords}&nbsp;
                <strong>Successfully Uploaded:</strong> {uploadResponse.successfullyUploaded}&nbsp;
                <strong>Total Failed Records:</strong> {uploadResponse.totalFailedRecords}&nbsp;
                <strong>Total Duplicated Records:</strong> {uploadResponse.totalDuplicatedRecords}
              </Typography>

              {uploadResponse.deviceUploads.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      Device Uploads
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        title="Search"
                        onClick={() => setShowSearch(!showSearch)}
                        sx={{ color: '#757575' }}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                      <CSVLink
                        data={csvData}
                        filename="upload_response.csv"
                        style={{ textDecoration: 'none' }}
                      >
                        <IconButton size="small" title="Download" sx={{ color: '#757575' }}>
                          <Download fontSize="small" />
                        </IconButton>
                      </CSVLink>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Filter</InputLabel>
                        <Select
                          value={filterError}
                          onChange={(e) => setFilterError(e.target.value as string)}
                          label="Filter"
                          sx={{ height: 32 }}
                        >
                          <MenuItem value="all">All Errors</MenuItem>
                          <MenuItem value="imei">IMEI Errors</MenuItem>
                          <MenuItem value="serial">Serial Errors</MenuItem>
                          <MenuItem value="duplicate">Duplicate Errors</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {showSearch && (
                    <TextField
                      fullWidth
                      placeholder="Search by IMEI or Serial No"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 2 }}
                      size="small"
                    />
                  )}

                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#E50019' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>IMEI</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Serial No</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Device Make</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredData.map((device, index) => (
                          <TableRow
                            key={index}
                            hover
                            sx={{
                              '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                              '&:hover': { backgroundColor: '#f0f0f0' }
                            }}
                          >
                            <TableCell>{device.IMEI}</TableCell>
                            <TableCell>{device.serialNo}</TableCell>
                            <TableCell>{device.deviceMake}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{device.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResponseClose}
            variant="contained"
            sx={{
              backgroundColor: '#E60012',
              '&:hover': { backgroundColor: '#cc0010' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkUploadDialog;